import { useEffect, useRef } from "react";
import type { ICursorPositionNode, ICursorPosition } from "features/editor/types/cursor";

const isTextNode = (node: Node | null | undefined): boolean => Boolean(node?.nodeType === 3);
const isMacroNode = (node: Node | null | undefined): boolean =>
  Boolean((node?.parentNode as Element)?.classList?.contains("macro"));

const getNodeInRootFromLeaf = (root: HTMLElement, node: Node): Node => {
  if (node.parentNode === root) {
    return node;
  }

  return getNodeInRootFromLeaf(root, node.parentNode!);
};

const getTextNodeAtPosition = (root: HTMLElement, index: number): ICursorPositionNode => {
  const treeWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let currentNode = treeWalker.nextNode();
  let position = index;

  while (currentNode) {
    if (currentNode.textContent === null) {
      break;
    }

    if (
      isTextNode(currentNode) && !isMacroNode(currentNode)
        ? position <= currentNode.textContent.length
        : position < currentNode.textContent.length
    ) {
      currentNode = getNodeInRootFromLeaf(root, currentNode);
      return { node: currentNode, position };
    }
    position -= currentNode.textContent.length;
    currentNode = treeWalker.nextNode();
  }

  // Handle the case where the index is between nodes
  currentNode = treeWalker.currentNode;
  return { node: currentNode, position: currentNode?.textContent?.length ?? 0 };
};

const setRange = ({ startNode, startOffset, endNode, endOffset, collapse }: any) => {
  if (!startNode) {
    return;
  }

  const range = document.createRange();
  range.setStart(startNode, startOffset);
  range.setEnd(endNode ?? startNode, endOffset ?? 0);

  if (collapse) {
    range.collapse(true);
  }

  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
};

export const useCursorPosition = (contentEditableRef: React.RefObject<HTMLDivElement>) => {
  const cursorPositionRef = useRef<ICursorPosition | null>(null);

  const saveCursorPosition = () => {
    if (!contentEditableRef.current) return;

    const selection = window.getSelection();

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      if (contentEditableRef.current === range.startContainer) {
        const isRootEmpty = contentEditableRef.current.childNodes.length === 0;
        if (isRootEmpty || range.startOffset === 0) {
          cursorPositionRef.current = {
            isRootStart: true,
          };
        } else {
          cursorPositionRef.current = {
            startOffset: range.startOffset, // For some reason, the range.startOffset is always 1 more than the actual index
          };
        }
      } else {
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(contentEditableRef.current);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        const start = preSelectionRange.toString().length;

        cursorPositionRef.current = {
          start,
          end: start + range.toString().length,
        };
      }
    }
  };

  const restoreCursorPosition = () => {
    if (!cursorPositionRef.current || !contentEditableRef.current) {
      return;
    }

    const isRootStart = "isRootStart" in cursorPositionRef.current;
    let startNode;
    let endNode;
    let collapse = false;

    if (isRootStart) {
      startNode = { node: contentEditableRef.current, position: 0 };
      endNode = startNode;
    } else if ("startOffset" in cursorPositionRef.current) {
      startNode = {
        node: contentEditableRef.current,
        position: cursorPositionRef.current.startOffset,
      };
      endNode = startNode;

      collapse = true;
    } else if ("start" in cursorPositionRef.current) {
      startNode = getTextNodeAtPosition(contentEditableRef.current, cursorPositionRef.current.start);
      endNode = getTextNodeAtPosition(contentEditableRef.current, cursorPositionRef.current.end);
    }

    setRange({
      startNode: startNode?.node,
      startOffset: startNode?.position,
      endNode: endNode?.node,
      endOffset: endNode?.position,
      collapse,
    });
  };

  const hasCursorPosition = () => Boolean(cursorPositionRef.current !== null);

  const setCursorAfterElement = (element: HTMLElement) => {
    const selection = window.getSelection();

    if (!element || !selection || !hasCursorPosition()) {
      return;
    }

    const nextNode = element.nextSibling;

    // If the next node is empty text node, it will be removed by the browser.
    if (isTextNode(nextNode) && (nextNode as Text)?.length > 0) {
      setRange({
        startNode: nextNode,
        startOffset: 0,
      });
    } else {
      const newRange = document.createRange();
      newRange.setStartAfter(element);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    saveCursorPosition();
  };

  useEffect(() => {
    const handleMouseup = () => {
      saveCursorPosition();
    };

    contentEditableRef.current?.addEventListener("mouseup", handleMouseup);

    return () => {
      contentEditableRef.current?.removeEventListener("mouseup", handleMouseup);
    };
  }, []);

  const removeCursorPosition = () => {
    cursorPositionRef.current = null;
  };

  return {
    saveCursorPosition,
    restoreCursorPosition,
    removeCursorPosition,
    hasCursorPosition,
    setCursorAfterElement,
  };
};
